import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="tactile-score"
export default class extends Controller {
  static targets = [
    "team1CurrentScore", "team2CurrentScore",
    "team1History", "team2History",
    "setIndicator", "setsWon",
    "finishModal", "finalScore", "validateButton",
    "team1Plus", "team1Minus", "team2Plus", "team2Minus",
    "scoreTeam1Set1", "scoreTeam2Set1",
    "scoreTeam1Set2", "scoreTeam2Set2",
    "scoreTeam1Set3", "scoreTeam2Set3"
  ]

  static values = {
    format: String,  // A1, A2, B1, B2, C1, C2, D1, D2, E
    matchId: Number,  // ID du match pour les mises à jour live
    liveScore: Object  // Score en cours depuis la base de données
  }

  connect() {
    // Charger le score existant ou partir de zéro
    const live = this.hasLiveScoreValue ? this.liveScoreValue : {}

    this.scores = {
      team1: {
        set1: live.team1_set1 || 0,
        set2: live.team1_set2 || 0,
        set3: live.team1_set3 || 0
      },
      team2: {
        set1: live.team2_set1 || 0,
        set2: live.team2_set2 || 0,
        set3: live.team2_set3 || 0
      }
    }
    this.currentSet = live.current_set || 1
    this.setsWonCount = {
      team1: live.sets_won_team1 || 0,
      team2: live.sets_won_team2 || 0
    }
    this.matchFinished = false
    this.isTieBreak = false

    // Déterminer les règles selon le format
    this.setRules()

    this.updateDisplay()
    this.updateSetIndicator()
  }

  setRules() {
    const format = this.formatValue || 'B1'

    // Formats à 9 jeux (D1/D2)
    if (format === 'D1' || format === 'D2') {
      this.gamesPerSet = 9
      this.tieBreakAt = 8  // Tie-break à 8-8
    }
    // Formats à 4 jeux (C1/C2)
    else if (format === 'C1' || format === 'C2') {
      this.gamesPerSet = 4
      this.tieBreakAt = 4  // Tie-break à 4-4
    }
    // Formats à 6 jeux (A1/A2/B1/B2/E)
    else {
      this.gamesPerSet = 6
      this.tieBreakAt = 6  // Tie-break à 6-6
    }

    // Set 3 = super TB pour B1/B2/C1/C2
    this.hasSet3SuperTB = ['B1', 'B2', 'C1', 'C2'].includes(format)
  }

  incrementTeam1() {
    if (!this.matchFinished) {
      this.updateScore('team1', 1)
    }
  }

  decrementTeam1() {
    if (!this.matchFinished) {
      this.updateScore('team1', -1)
    }
  }

  incrementTeam2() {
    if (!this.matchFinished) {
      this.updateScore('team2', 1)
    }
  }

  decrementTeam2() {
    if (!this.matchFinished) {
      this.updateScore('team2', -1)
    }
  }

  updateScore(team, delta) {
    const setKey = `set${this.currentSet}`
    const newScore = this.scores[team][setKey] + delta

    // Ne pas descendre en dessous de 0
    if (newScore < 0) return

    this.scores[team][setKey] = newScore
    this.updateDisplay()
    this.checkSetWin()

    // Envoyer le score live au serveur (avec debounce)
    this.sendLiveScoreDebounced()
  }

  checkSetWin() {
    const setKey = `set${this.currentSet}`
    const team1Score = this.scores.team1[setKey]
    const team2Score = this.scores.team2[setKey]

    // Vérifier tie-break (au seuil défini par le format)
    if (team1Score === this.tieBreakAt && team2Score === this.tieBreakAt && !this.isTieBreak) {
      this.isTieBreak = true
      this.updateSetIndicator()
      return
    }

    let winner = null

    // Règles de victoire
    if (this.isTieBreak) {
      // Tie-break classique (7 points, écart de 2)
      if (team1Score >= 7 && team1Score - team2Score >= 2) winner = 'team1'
      if (team2Score >= 7 && team2Score - team1Score >= 2) winner = 'team2'
    } else if (this.currentSet === 3 && this.hasSet3SuperTB) {
      // Set 3 = super tie-break (10 points, écart de 2)
      if (team1Score >= 10 && team1Score - team2Score >= 2) winner = 'team1'
      if (team2Score >= 10 && team2Score - team1Score >= 2) winner = 'team2'
    } else {
      // Set normal (N jeux selon format, écart de 2)
      if (team1Score >= this.gamesPerSet && team1Score - team2Score >= 2) winner = 'team1'
      if (team2Score >= this.gamesPerSet && team2Score - team1Score >= 2) winner = 'team2'
    }

    if (winner) {
      this.setsWonCount[winner]++
      this.isTieBreak = false

      // Format D1/D2 = 1 set uniquement
      const isOneSetFormat = this.formatValue === 'D1' || this.formatValue === 'D2'

      // Match terminé ?
      if (isOneSetFormat || this.setsWonCount[winner] === 2) {
        this.finishMatch()
      } else if (this.currentSet < 3) {
        // Passer au set suivant
        this.currentSet++
        this.updateDisplay()
        this.updateSetIndicator()
      }
    }
  }

  finishMatch() {
    this.matchFinished = true
    this.disableAllButtons()
    this.syncFormData()
    this.showFinishModal()
  }

  disableAllButtons() {
    this.team1PlusTarget.disabled = true
    this.team1MinusTarget.disabled = true
    this.team2PlusTarget.disabled = true
    this.team2MinusTarget.disabled = true
  }

  syncFormData() {
    this.scoreTeam1Set1Target.value = this.scores.team1.set1
    this.scoreTeam2Set1Target.value = this.scores.team2.set1
    this.scoreTeam1Set2Target.value = this.scores.team1.set2 || ''
    this.scoreTeam2Set2Target.value = this.scores.team2.set2 || ''
    this.scoreTeam1Set3Target.value = this.scores.team1.set3 || ''
    this.scoreTeam2Set3Target.value = this.scores.team2.set3 || ''
  }

  showFinishModal() {
    this.finishModalTarget.classList.remove('hidden')
    this.finalScoreTarget.textContent = `Score final : ${this.setsWonCount.team1} - ${this.setsWonCount.team2}`
  }

  updateDisplay() {
    const setKey = `set${this.currentSet}`

    // Score actuel
    this.team1CurrentScoreTarget.textContent = this.scores.team1[setKey]
    this.team2CurrentScoreTarget.textContent = this.scores.team2[setKey]

    // Historique des sets
    this.updateHistory()
  }

  updateHistory() {
    const team1History = []
    const team2History = []

    for (let i = 1; i <= 3; i++) {
      if (i < this.currentSet || this.matchFinished) {
        const score1 = this.scores.team1[`set${i}`]
        const score2 = this.scores.team2[`set${i}`]
        if (score1 > 0 || score2 > 0) {
          team1History.push(score1)
          team2History.push(score2)
        }
      }
    }

    this.team1HistoryTarget.innerHTML = team1History.map(s => `<span>${s}</span>`).join('<span>-</span>')
    this.team2HistoryTarget.innerHTML = team2History.map(s => `<span>${s}</span>`).join('<span>-</span>')
  }

  updateSetIndicator() {
    let label = ''

    if (this.isTieBreak) {
      // Tie-break classique à 7 points
      label = 'Jeu décisif - 7 pts'
    } else if (this.currentSet === 3 && this.hasSet3SuperTB) {
      // Set 3 = super tie-break (formats B1/B2/C1/C2)
      label = 'Super Tie-break - 10 pts'
    } else {
      // Set normal (4, 6 ou 9 jeux selon format)
      label = `Set ${this.currentSet} - ${this.gamesPerSet} jeux`
    }

    this.setIndicatorTarget.textContent = label
    this.setsWonTarget.textContent = `${this.setsWonCount.team1} - ${this.setsWonCount.team2}`
  }

  validate() {
    if (this.matchFinished) {
      this.syncFormData()
      // Le formulaire sera soumis automatiquement
    }
  }

  cancelFinish() {
    // Fermer le modal
    this.finishModalTarget.classList.add('hidden')

    // Réactiver les boutons
    this.team1PlusTarget.disabled = false
    this.team1MinusTarget.disabled = false
    this.team2PlusTarget.disabled = false
    this.team2MinusTarget.disabled = false

    // Réinitialiser le flag
    this.matchFinished = false

    // Revenir au dernier set en cours
    // On recalcule le currentSet en fonction des sets gagnés
    if (this.setsWonCount.team1 === 1 || this.setsWonCount.team2 === 1) {
      // On était au set 3
      this.currentSet = 3
      this.setsWonCount.team1 = this.setsWonCount.team1 > 0 ? this.setsWonCount.team1 - 1 : 0
      this.setsWonCount.team2 = this.setsWonCount.team2 > 0 ? this.setsWonCount.team2 - 1 : 0
    } else {
      // On était au set 2 ou 1
      this.currentSet = this.currentSet > 1 ? this.currentSet : 1
      this.setsWonCount.team1 = 0
      this.setsWonCount.team2 = 0
    }

    this.updateDisplay()
    this.updateSetIndicator()
  }

  sendLiveScoreDebounced() {
    // Debounce pour éviter trop de requêtes
    clearTimeout(this.sendTimeout)
    this.sendTimeout = setTimeout(() => {
      this.sendLiveScore()
    }, 500)  // Attendre 500ms après le dernier changement
  }

  async sendLiveScore() {
    if (!this.hasMatchIdValue) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      await fetch(`/matches/${this.matchIdValue}/live_score`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          live_score: {
            current_set: this.currentSet,
            team1_set1: this.scores.team1.set1,
            team2_set1: this.scores.team2.set1,
            team1_set2: this.scores.team1.set2,
            team2_set2: this.scores.team2.set2,
            team1_set3: this.scores.team1.set3,
            team2_set3: this.scores.team2.set3,
            sets_won_team1: this.setsWonCount.team1,
            sets_won_team2: this.setsWonCount.team2
          }
        })
      })
    } catch (error) {
      console.error('Erreur lors de l\'envoi du score live:', error)
    }
  }
}
